import { useEffect, useRef, useState } from 'react';
import { useHref } from 'react-router-dom';
import { READY, DRAFT, DEVICE_WIDTH, isPreviewMessage, previewScale } from '../../lib/previewFrame';

/**
 * The Studio area's draft rendered in a same-origin iframe at a real phone (390 px) or desktop
 * (1280 px) width, scaled down to fit its column, so media queries show the true layout (D51).
 * `catalog` is the store's published listings the storefront previews fill in with (B15).
 * `height` is the visible box height in CSS px.
 */
export default function PreviewFrame({ area, doc, theme, catalog, device, height }) {
  const src = useHref('/appearance/frame');
  const box = useRef(null);
  const frame = useRef(null);
  const [width, setWidth] = useState(0);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const el = box.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The frame asks for the draft when it mounts (READY); after that every edit is pushed.
  useEffect(() => {
    const onMessage = (e) => {
      if (frame.current && isPreviewMessage(e, { type: READY, origin: window.location.origin, source: frame.current.contentWindow })) setReady(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);
  useEffect(() => {
    if (ready && frame.current) frame.current.contentWindow.postMessage({ type: DRAFT, area, doc, theme, catalog }, window.location.origin);
  }, [ready, area, doc, theme, catalog]);

  const vw = DEVICE_WIDTH[device] || DEVICE_WIDTH.desktop;
  const phone = device === 'phone';
  // A phone gets a bezel; inside it the screen is 390 px wide.
  const available = phone ? Math.max(0, width - 16) : width;
  const scale = previewScale(device, available);
  const frameHeight = Math.max(0, (phone ? height - 16 : height) / scale);

  return (
    <div ref={box} className="flex w-full min-w-0 justify-center overflow-hidden rounded-card bg-[var(--app-bg)]" style={{ height }}>
      {/* Nothing until the column is measured: a guessed width would size the column itself. */}
      {width > 0 ? (
      <div
        className={phone ? 'rounded-sheet border-8 border-foreground/90 bg-surface shadow-lg overflow-hidden' : 'w-full overflow-hidden border border-[var(--app-border)] rounded-card'}
        style={{ width: vw * scale + (phone ? 16 : 0), height: phone ? frameHeight * scale + 16 : height }}
      >
        <iframe
          ref={frame}
          src={src}
          title={phone ? 'Phone preview of your storefront' : 'Desktop preview of your storefront'}
          data-testid="appearance-preview-frame"
          data-device={device}
          className="block origin-top-left border-0 bg-surface"
          style={{ width: vw, height: frameHeight, transform: `scale(${scale})` }}
        />
      </div>
      ) : null}
    </div>
  );
}
